import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "mb-4 flex flex-wrap items-end gap-3" }
const _hoisted_2 = {
  key: 0,
  class: "mb-4 text-sm text-red-600",
  role: "alert"
}
const _hoisted_3 = {
  key: 0,
  class: "divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white"
}

import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

import EmptyState from '@/components/neue/EmptyState.vue'
import InputField from '@/components/neue/InputField.vue'
import NeueLoading from '@/components/neue/NeueLoading.vue'
import NeueNumberedPagination from '@/components/neue/NeueNumberedPagination.vue'
import SegmentedControl from '@/components/neue/SegmentedControl.vue'
import ParsleyProductRow from '@/components/parsley/ParsleyProductRow.vue'
import { isPastLastPage } from '@/helpers/parsley'
import { useParsleyRunTracker } from '@/helpers/parsleyRunTracker'
import { errorMessage, parsleyApi } from '@/services/parsleyApi'

const PAGE_SIZE = 25
const SEARCH_DELAY_MS = 300
const PENDING_REFRESH_MS = 3000
const MAX_PENDING_REFRESHES = 10
const INTRO =
  'Link each product to the Parsley item it is made from. Nutrition, ingredients and allergens then come from Parsley. Suggestions are only a starting point: check the portion before you link.'
const EMPTY_NOTE = 'No products match these filters.'
const LOAD_ERROR = "Couldn't load your products. Please refresh the page."

export default {
  __name: 'ParsleyProductsTab',
  setup(__props) {

const SCOPE_OPTIONS = [
  { name: 'Live in a store', value: 'live' },
  { name: 'All products', value: 'all' }
]
const LINKED_OPTIONS = [
  { name: 'All', value: '' },
  { name: 'Linked', value: 'true' },
  { name: 'Not linked', value: 'false' }
]

const scope = ref('live')
const linked = ref('')
const search = ref('')
const page = ref(1)
const matches = ref([])
const pageCount = ref(0)
const totalCount = ref(0)
const loading = ref(true)
const error = ref('')

const { finished } = useParsleyRunTracker()

let searchTimer = null
let refreshTimer = null
let refreshes = 0
let latest = 0

const params = () => ({
  live: scope.value === 'all' ? 'false' : undefined,
  linked: linked.value,
  search: String(search.value ?? '').trim(),
  page: page.value,
  page_size: PAGE_SIZE
})

const stopRefreshing = () => {
  if (refreshTimer) clearTimeout(refreshTimer)
  refreshTimer = null
}

const hasPendingLink = () => matches.value.some(row => row.attributes?.link?.status === 'pending')

// The per-link job is not a tracked run, so nothing else says when a link
// waiting to sync has synced or failed.
const scheduleRefresh = () => {
  if (refreshes >= MAX_PENDING_REFRESHES || !hasPendingLink()) return
  refreshTimer = setTimeout(() => {
    refreshTimer = null
    refreshes += 1
    load({ background: true })
  }, PENDING_REFRESH_MS)
}

// A background refresh leaves the loading state and any error alone, and a
// failure only counts as one of the refreshes.
const load = async ({ background = false } = {}) => {
  stopRefreshing()
  const mine = (latest += 1)
  if (!background) {
    loading.value = true
    error.value = ''
  }
  try {
    const result = await parsleyApi.productMatches(params())
    if (mine !== latest) return
    if (isPastLastPage(result, page.value)) {
      page.value = Math.max(result.total_pages, 1)
      load({ background })
      return
    }
    matches.value = result.data
    pageCount.value = result.total_pages
    totalCount.value = result.total_records
    scheduleRefresh()
  } catch (failure) {
    if (mine !== latest) return
    if (background) scheduleRefresh()
    else error.value = errorMessage(failure, LOAD_ERROR)
  } finally {
    if (mine === latest && !background) loading.value = false
  }
}

// What the merchant does starts the count of refreshes again.
const reload = () => {
  refreshes = 0
  load()
}

const loadFirstPage = () => {
  page.value = 1
  reload()
}

const goTo = next => {
  page.value = next
  reload()
}

watch([scope, linked], loadFirstPage)

watch(search, () => {
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(loadFirstPage, SEARCH_DELAY_MS)
})

// A sync that finishes changes statuses on this page.
watch(finished, isFinished => {
  if (isFinished) load()
})

onMounted(load)

onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer)
  stopRefreshing()
  latest += 1
})

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("p", { class: "mb-4 max-w-2xl text-sm text-gray-600" }, _toDisplayString(INTRO)),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(SegmentedControl, {
        modelValue: scope.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((scope).value = $event)),
        options: SCOPE_OPTIONS
      }, null, 8, ["modelValue"]),
      _createVNode(SegmentedControl, {
        modelValue: linked.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((linked).value = $event)),
        options: LINKED_OPTIONS
      }, null, 8, ["modelValue"]),
      _createVNode(InputField, {
        modelValue: search.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => ((search).value = $event)),
        placeholder: "Search products",
        class: "w-64"
      }, null, 8, ["modelValue"])
    ]),
    (error.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    _createVNode(NeueLoading, {
      loading: loading.value && !matches.value.length
    }, {
      default: _withCtx(() => [
        (matches.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(matches.value, (match) => {
                return (_openBlock(), _createBlock(ParsleyProductRow, {
                  key: match.id,
                  match: match,
                  onChanged: reload
                }, null, 8, ["match"]))
              }), 128))
            ]))
          : (!loading.value && !error.value)
            ? (_openBlock(), _createBlock(EmptyState, {
                key: 1,
                plural: "products",
                description: EMPTY_NOTE
              }))
            : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["loading"]),
    _createVNode(NeueNumberedPagination, {
      page: page.value,
      "page-count": pageCount.value,
      "total-count": totalCount.value,
      "page-size": PAGE_SIZE,
      loading: loading.value,
      onChange: goTo
    }, null, 8, ["page", "page-count", "total-count", "loading"])
  ]))
}
}

}